import React, { useCallback, useState } from 'react';
import propTypes from 'prop-types';
import { Box, Grid } from '@material-ui/core';
import { IoIosArrowDown } from 'react-icons/io';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { isSameDay, startOfWeek, endOfWeek } from 'date-fns';

import Boton from '../Button';
import ExportarDocumentos from '../ExportarDocumento';
import Select from '../Select';
import Typography from '../Typography';

import { CamposValidados, customCabecero } from '../../utilidades/functions';
import { DatePickerTypes } from '../../constantes/datePickerTypes';
import { mapaDependenciasPorDefecto } from '../../constantes/reportes';

import styles from './styles';

/**
 * Componente ListItem para elementos de los filtros Select
 * @param {Object} props
 * @param {string} props.alias
 * @param {string} props.nombre
 * @param {string} props.descripcion
 * @param {string} props.nombreSitio
 * @param {string} props.nombreInvernadero
 * @param {string} props.nombreNave
 * @param {string} props.nombreTabla
 * @returns Componente
 */
const ListItem = ({
  alias,
  nombre,
  descripcion,
  nombreSitio,
  nombreInvernadero,
  nombreNave,
  nombreTabla,
}) => {
  const detalles = [
    descripcion,
    nombreSitio && `Sitio: ${nombreSitio}`,
    nombreInvernadero && `Invernadero: ${nombreInvernadero}`,
    nombreNave && `Nave: ${nombreNave}`,
    nombreTabla && `Tabla: ${nombreTabla}`,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <Box>
      <Typography component="p">{alias || nombre}</Typography>
      {detalles && (
        <Typography
          style={{ color: '#aaa', margin: 0 }}
          component="span"
          variant="caption"
        >
          {detalles}
        </Typography>
      )}
    </Box>
  );
};

const CabeceroReportes = ({
  data,
  formData,
  buscar,
  onChange,
  onDelete,
  handleTodosSelected,
  endpoint,
  selects,
  method,
  mapaDependencias = mapaDependenciasPorDefecto,
}) => {
  const classes = styles();
  const [formErrors] = useState({});
  const [anchorConfirmacion, setAnchorConfirmacion] = useState(null);
  const [documento, setDocumento] = useState({});
  const cabecero = customCabecero(selects);
  const {
    sitios = [],
    fenologias = [],
    parametros = [],
    enfermedades = [],
    saneos = [],
    niveles = [],
    nivelInfecciones = [],
  } = data;

  /**
   * Función que se encarga de validar el formulario
   */
  const ValidarData = useCallback(() => CamposValidados(formData), [formData]);

  /**
   * Función que se encarga de exportar la información
   * @param {*} event
   */
  const exportar = (event) => {
    if (ValidarData()) return;
    const params = handleTodosSelected();
    setDocumento({ params, endpoint, method });
    setAnchorConfirmacion(event.currentTarget);
  };

  const onClose = () => {
    setAnchorConfirmacion(null);
  };

  const fechaChange = (date) => {
    onChange({ target: { name: 'fecha', value: date } });
  };

  const incluirValoresCeroChange = (_event, nuevoValor) => {
    onChange({ target: { name: 'incluirValoresCero', value: nuevoValor } });
  };

  /**
   * Función que se encarga de filtrar los elementos según los padres seleccionados
   * @param {string} clave
   * @returns Elementos filtrados
   */
  const filtrarPorDependencias = useCallback(
    (clave) => {
      const dependencia = mapaDependencias[clave];
      if (!dependencia) return [];

      const ancestros = [clave, ...dependencia.ancestros];
      let clavePadre = ancestros.pop();
      let elementosFiltrados = [];

      while (ancestros.length > 0) {
        const claveActual = ancestros.pop();
        const datosActuales = data?.[claveActual] ?? [];
        const filtroPadre = formData?.[clavePadre] ?? [];
        const incluirTodos = filtroPadre.includes(0);
        const claveRelacional = mapaDependencias[claveActual].claveRelacional;

        if (!filtroPadre.length) {
          elementosFiltrados = [];
          break;
        }

        if (incluirTodos) {
          if (elementosFiltrados.length) {
            const idsPadres = new Set(elementosFiltrados.map((e) => e.id));
            const condicionFn = ({ [claveRelacional]: id }) =>
              idsPadres.has(id);
            elementosFiltrados = datosActuales.filter(condicionFn);
          } else {
            elementosFiltrados = datosActuales;
          }
        } else {
          const condicionFn = ({ [claveRelacional]: id }) =>
            filtroPadre.includes(id);
          elementosFiltrados = datosActuales.filter(condicionFn);
        }

        clavePadre = claveActual;
      }

      return elementosFiltrados;
    },
    [data, formData, mapaDependencias],
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={1}>
        {cabecero?.sitio.show && (
          <Grid item xs={12} sm={cabecero.sitio.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Sitio"
              required
              options={sitios}
              onChange={onChange}
              name="sitios"
              value={formData.sitios}
              multiple
              onDelete={onDelete}
              labelProp="nombre"
              error={formErrors.sitios}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.invernadero.show && (
          <Grid item xs={12} sm={cabecero.invernadero.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Invernadero"
              required
              options={filtrarPorDependencias('invernaderos')}
              onChange={onChange}
              name="invernaderos"
              value={formData.invernaderos || []}
              labelProp="nombre"
              multiple
              onDelete={onDelete}
              error={formErrors.invernaderos}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.nave.show && (
          <Grid item xs={12} sm={cabecero.nave.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Nave"
              required
              options={filtrarPorDependencias('naves')}
              onChange={onChange}
              name="naves"
              value={formData.naves || []}
              labelProp="nombre"
              multiple
              onDelete={onDelete}
              error={formErrors.naves}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.invernaderoNave.show && (
          <Grid item xs={12} sm={cabecero.invernaderoNave.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Invernadero - Nave"
              required
              options={filtrarPorDependencias('naves')}
              onChange={onChange}
              name="naves"
              value={formData.naves}
              labelProp="nombre"
              multiple
              onDelete={onDelete}
              error={formErrors.naves}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.seccion.show && (
          <Grid item xs={12} sm={cabecero.seccion.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Seccion"
              required
              options={filtrarPorDependencias('secciones')}
              onChange={onChange}
              name="secciones"
              value={formData.secciones || []}
              multiple
              onDelete={onDelete}
              labelProp="nombre"
              error={formErrors.secciones}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.tabla.show && (
          <Grid item xs={12} sm={cabecero.tabla.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Tablas"
              required
              options={filtrarPorDependencias('tablas')}
              labelProp="nombre"
              onChange={onChange}
              name="tablas"
              multiple
              onDelete={onDelete}
              value={formData['tablas'] || []}
              error={formErrors['tablas']}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.trampa.show && (
          <Grid item xs={12} sm={cabecero.trampa.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Trampas"
              required
              options={filtrarPorDependencias('trampas')}
              onChange={onChange}
              name="trampas"
              value={formData.trampas || []}
              multiple
              onDelete={onDelete}
              labelProp="alias"
              error={formErrors.trampas}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.planta.show && (
          <Grid item xs={12} sm={cabecero.planta.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Plantas"
              required
              options={filtrarPorDependencias('plantas')}
              labelProp="nombre"
              onChange={onChange}
              name="plantas"
              multiple
              onDelete={onDelete}
              value={formData['plantas'] || []}
              error={formErrors['plantas']}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.surco.show && (
          <Grid item xs={12} sm={cabecero.surco.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Surco"
              required
              options={filtrarPorDependencias('surcos')}
              labelProp="nombre"
              onChange={onChange}
              name="surcos"
              multiple
              onDelete={onDelete}
              value={formData.surcos || []}
              error={formErrors.surcos}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.fenologia.show && (
          <Grid item xs={12} sm={cabecero.fenologia.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Fenologías"
              required
              options={fenologias || []}
              labelProp="nombre"
              onChange={onChange}
              name="fenologias"
              multiple
              onDelete={onDelete}
              value={formData['fenologias'] || []}
              error={formErrors['fenologias']}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
            />
          </Grid>
        )}
        {cabecero.parametro.show && (
          <Grid item xs={12} sm={cabecero.parametro.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Parámetros"
              required
              options={parametros || []}
              labelProp="nombre"
              onChange={onChange}
              name="parametros"
              multiple
              onDelete={onDelete}
              value={formData['parametros'] || []}
              error={formErrors['parametros']}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.enfermedad.show && (
          <Grid item xs={12} sm={cabecero.enfermedad.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Plaga/Enfermedad"
              required
              options={enfermedades || []}
              labelProp="nombre"
              onChange={onChange}
              name="enfermedades"
              multiple
              onDelete={onDelete}
              value={formData.enfermedades || []}
              error={formErrors.enfermedades}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.saneo.show && (
          <Grid item xs={12} sm={cabecero.saneo.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Saneo"
              required
              options={saneos || []}
              labelProp="nombre"
              onChange={onChange}
              name="saneos"
              multiple
              onDelete={onDelete}
              value={formData.saneos || []}
              error={formErrors.saneos}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.nivel.show && (
          <Grid item xs={12} sm={cabecero.nivel.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Nivel"
              required
              options={niveles || []}
              labelProp="nombre"
              onChange={onChange}
              name="niveles"
              multiple
              onDelete={onDelete}
              value={formData.niveles || []}
              error={formErrors.niveles}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.nivelInfeccion.show && (
          <Grid item xs={12} sm={cabecero.nivelInfeccion.width}>
            <Select
              enableSelectAll
              className={classes.textField}
              label="Nivel de infección"
              required
              options={nivelInfecciones || []}
              labelProp="nombre"
              onChange={onChange}
              name="nivelInfecciones"
              multiple
              onDelete={onDelete}
              value={formData.nivelInfecciones || []}
              error={formErrors.nivelInfecciones}
              IconComponent={(props) => <IoIosArrowDown {...props} size={20} />}
              customLabel={ListItem}
            />
          </Grid>
        )}
        {cabecero.fecha.show && (
          <Grid item xs={12} sm={cabecero.fecha.width} style={{ marginTop: 5 }}>
            <Typography className={classes.label}>Semana *</Typography>
            <KeyboardDatePicker
              id="semanaAnio"
              value={formData.fecha}
              variant="inline"
              disableToolbar
              format={
                cabecero.fecha.type === DatePickerTypes.week
                  ? "'Semana' II 'del' RRRR"
                  : "dd 'de' MMMM 'del' yyyy"
              }
              onChange={fechaChange}
              fullWidth
              InputProps={{
                disableUnderline: true,
                disabled: true,
                classes: {
                  root: classes.input,
                },
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
              PopoverProps={{
                classes: {
                  paper: classes.datePaper,
                },
              }}
              renderDay={(
                day,
                selectedDate,
                dayInCurrentMonth,
                dayComponent,
              ) => {
                if (cabecero.fecha.type === DatePickerTypes.week) {
                  const start = startOfWeek(selectedDate, {
                    weekStartsOn: 1,
                  });
                  const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
                  const dayIsBetween = day >= start && day <= end;

                  return React.cloneElement(dayComponent, {
                    style: {
                      ...dayComponent.style,
                      backgroundColor: dayIsBetween ? '#66aea4' : undefined,
                      color: dayIsBetween ? '#fff' : undefined,
                    },
                  });
                }

                const sameDate = isSameDay(day, selectedDate);
                return React.cloneElement(dayComponent, {
                  style: {
                    ...dayComponent.style,
                    backgroundColor: sameDate ? '#66aea4' : undefined,
                    color: sameDate ? '#fff' : undefined,
                  },
                });
              }}
              disableFuture
              maxDateMessage="La fecha debe ser menor a la fecha actual"
            />
          </Grid>
        )}
        {cabecero.valoresCero?.show && (
          <Grid
            item
            xs={12}
            sm={cabecero.valoresCero.width}
            style={{ alignSelf: 'flex-end' }}
          >
            <Typography className={classes.label} display="block">
              Valores en cero *
            </Typography>
            <ToggleButtonGroup
              aria-label="Inclusión de ceros"
              exclusive
              value={formData.incluirValoresCero ?? true}
              onChange={incluirValoresCeroChange}
              classes={{ root: classes.toggleButtonGroup }}
            >
              <ToggleButton
                classes={{ root: classes.toggleButton }}
                value={true}
                aria-label="incluir ceros"
              >
                Incluir
              </ToggleButton>
              <ToggleButton
                classes={{ root: classes.toggleButton }}
                value={false}
                aria-label="excluir ceros"
              >
                Excluir
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        )}
        <Grid item xs={3}>
          <Box
            display="flex"
            gridGap={4}
            width="100%"
            height="100%"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            <Boton
              onClick={buscar}
              label="Buscar"
              classesCustom={{ root: classes.btnContainer, boton: classes.btn }}
            />
            <Boton
              onClick={(e) => exportar(e)}
              label="Exportar"
              classesCustom={{ root: classes.btnContainer, boton: classes.btn }}
              border
            />
            <ExportarDocumentos
              open={anchorConfirmacion}
              onClose={onClose}
              documento={documento}
            />
          </Box>
        </Grid>
      </Grid>
    </div>
  );
};

CabeceroReportes.propTypes = {
  /* datos de los selects */
  data: propTypes.object,
  /* Datos del los formularios */
  formData: propTypes.object,
  /* Función para buscar */
  buscar: propTypes.func,
  /* Función para obtener datos */
  onChange: propTypes.func,
  /* Función para elminar datos */
  onDelete: propTypes.func,
  /* Función donde recibe los parametros para buscar*/
  handleTodosSelected: propTypes.func,
  /* Endpoint del reporte */
  endpoint: propTypes.string,
  /* Objeto para mostrar los selects */
  selects: propTypes.object,
  /* Metodo de la petición */
  method: propTypes.string,
};

CabeceroReportes.defaultProps = {
  data: {},
  formData: {},
  buscar: null,
  onChange: null,
  onDelete: null,
  handleTodosSelected: null,
  endpoint: '',
  selects: {},
};

export default CabeceroReportes;
